import React, { useState } from 'react';
import {
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import { HelpOutline as HelpOutlineIcon } from '@mui/icons-material';
import { getHelpDoc } from './clickhouseHelpDocs';

/**
 * A small "?" icon that opens a modal explaining what a specific feature
 * does and how to use it. All docs live in {@link clickhouseHelpDocs.js}
 * keyed by {@code docKey} — never duplicate copy inline.
 *
 * Render it right next to any button/field/tab header:
 * {@code <HelpIcon docKey="deploy_binary" />}
 *
 * Why a central registry: the UI is rebuilt often, docs are edited rarely,
 * and we want every "?" on the page to pull from the same canonical source
 * so operators get consistent phrasing and we can audit coverage at a glance.
 */
const HelpIcon = ({ docKey, size = 'small', sx = {} }) => {
  const [open, setOpen] = useState(false);
  const doc = getHelpDoc(docKey);

  return (
    <>
      <Tooltip title={`About: ${doc.title}`} arrow>
        <IconButton
          size={size}
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
          sx={{
            color: 'text.secondary',
            padding: size === 'small' ? 0.25 : 0.5,
            '&:hover': { color: 'primary.main' },
            ...sx,
          }}
          aria-label={`Help: ${doc.title}`}
        >
          <HelpOutlineIcon fontSize={size === 'small' ? 'small' : 'medium'} />
        </IconButton>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HelpOutlineIcon color="primary" />
            <Typography variant="h6" component="span">
              {doc.title}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <HelpDocBody lines={doc.body} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} variant="contained">
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/**
 * Renders a doc body. Each array element is one "line":
 * - empty string → paragraph break
 * - line starting with '- ' or '* ' → bullet
 * - line starting with digit+dot → numbered bullet (rendered as indented paragraph)
 * - everything else → paragraph
 *
 * Inline formatting we support: `**bold**` and `` `code` ``. Not a full
 * markdown renderer — just enough to make the docs readable without pulling
 * in a dependency.
 */
const HelpDocBody = ({ lines }) => {
  const blocks = [];
  let bulletBuffer = [];

  const flushBullets = () => {
    if (bulletBuffer.length === 0) return;
    const items = bulletBuffer;
    bulletBuffer = [];
    blocks.push(
      <Box component="ul" sx={{ pl: 3, my: 1 }} key={`ul-${blocks.length}`}>
        {items.map((item, i) => (
          <li key={i}>
            <Typography variant="body2" component="span" sx={{ lineHeight: 1.6 }}>
              {renderInline(item)}
            </Typography>
          </li>
        ))}
      </Box>
    );
  };

  (lines || []).forEach((raw, idx) => {
    if (raw === '') {
      flushBullets();
      return;
    }
    const trimmed = raw.replace(/^\s+/, '');
    if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      bulletBuffer.push(trimmed.slice(2));
      return;
    }
    flushBullets();
    blocks.push(
      <Typography
        key={`p-${idx}`}
        variant="body2"
        sx={{ lineHeight: 1.65, mb: 0.75, whiteSpace: 'pre-wrap' }}
      >
        {renderInline(raw)}
      </Typography>
    );
  });
  flushBullets();

  return <Box>{blocks}</Box>;
};

/**
 * Replace **bold** and `code` with styled spans. Walks the string once.
 * Keeps it simple — nested/overlapping spans are not a concern for our
 * short doc bodies.
 */
function renderInline(text) {
  const parts = [];
  const regex = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let match;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    const token = match[0];
    if (token.startsWith('**')) {
      parts.push(
        <strong key={`b-${key++}`}>{token.slice(2, -2)}</strong>
      );
    } else {
      parts.push(
        <code
          key={`c-${key++}`}
          style={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: '0.85em',
            background: 'rgba(127,127,127,0.15)',
            padding: '1px 5px',
            borderRadius: 3,
          }}
        >
          {token.slice(1, -1)}
        </code>
      );
    }
    last = regex.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

export default HelpIcon;
